/**
 * HireBroker currency module
 * ----------------------
 * Canonical storage currency: UGX (backend price field is UGX).
 * Display currency: user preference UGX | USD.
 * Agent entry currency: UGX | USD (converted to UGX before save).
 */

export const CURRENCIES = {
  UGX: {
    code: 'UGX',
    name: 'Ugandan Shilling',
    symbol: 'UGX',
    locale: 'en-UG',
    decimals: 0,
  },
  USD: {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
    locale: 'en-US',
    decimals: 2,
  },
};

export const CURRENCY_CODES = Object.keys(CURRENCIES);

/** Default mid-market style rate: 1 USD = N UGX (configurable) */
export const DEFAULT_USD_TO_UGX = 3700;

const STORAGE_KEY = 'hirebroker_display_currency';
const RATE_STORAGE_KEY = 'hirebroker_usd_to_ugx';

export function getStoredCurrency() {
  try {
    const code = localStorage.getItem(STORAGE_KEY);
    if (code && CURRENCIES[code]) return code;
  } catch {
    /* ignore */
  }
  return 'UGX';
}

export function storeCurrency(code) {
  try {
    if (CURRENCIES[code]) localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* ignore */
  }
}

export function getUsdToUgxRate() {
  try {
    const raw = localStorage.getItem(RATE_STORAGE_KEY);
    const n = Number(raw);
    if (n > 0) return n;
  } catch {
    /* ignore */
  }
  return DEFAULT_USD_TO_UGX;
}

export function setUsdToUgxRate(rate) {
  const n = Number(rate);
  if (!(n > 0)) return;
  try {
    localStorage.setItem(RATE_STORAGE_KEY, String(n));
  } catch {
    /* ignore */
  }
}

/**
 * Convert amount between UGX and USD.
 * @param {number} amount
 * @param {string} from - UGX | USD
 * @param {string} to - UGX | USD
 * @param {number} [usdToUgx]
 */
export function convertAmount(amount, from, to, usdToUgx = getUsdToUgxRate()) {
  const n = Number(amount);
  if (amount == null || amount === '' || Number.isNaN(n)) return null;
  if (!from || !to || from === to) return n;

  const rate = usdToUgx > 0 ? usdToUgx : DEFAULT_USD_TO_UGX;

  // Normalize to UGX
  let ugx = n;
  if (from === 'USD') ugx = n * rate;
  else if (from !== 'UGX') return n;

  if (to === 'UGX') return ugx;
  if (to === 'USD') return ugx / rate;
  return ugx;
}

/**
 * Format a price for display in `displayCurrency`.
 * Assumes `amount` is in `sourceCurrency` (default UGX for stored listings).
 */
export function formatMoney(
  amount,
  {
    sourceCurrency = 'UGX',
    displayCurrency = 'UGX',
    usdToUgx = getUsdToUgxRate(),
    compact = false,
  } = {}
) {
  const converted = convertAmount(amount, sourceCurrency, displayCurrency, usdToUgx);
  if (converted == null) return 'Price on request';

  const meta = CURRENCIES[displayCurrency] || CURRENCIES.UGX;
  const value = meta.decimals === 0 ? Math.round(converted) : Number(converted);

  try {
    if (compact && value >= 1_000_000 && displayCurrency === 'UGX') {
      return `${meta.symbol} ${(value / 1_000_000).toFixed(1)}M`;
    }
    if (compact && value >= 1000 && displayCurrency === 'USD') {
      return `${meta.symbol}${(value / 1000).toFixed(1)}k`;
    }

    const formatted = new Intl.NumberFormat(meta.locale, {
      minimumFractionDigits: meta.decimals,
      maximumFractionDigits: meta.decimals,
    }).format(value);

    if (displayCurrency === 'USD') {
      return `${meta.symbol}${formatted}`;
    }
    return `${meta.symbol} ${formatted}`;
  } catch {
    return `${meta.symbol} ${value}`;
  }
}

/** Convert entry amount in agent-selected currency to UGX for API storage */
export function toStorageUgx(amount, entryCurrency = 'UGX', usdToUgx = getUsdToUgxRate()) {
  const converted = convertAmount(amount, entryCurrency, 'UGX', usdToUgx);
  if (converted == null) return null;
  return Math.round(converted);
}
